import { Stack, Typography } from "@mui/material";
import { FunctionComponent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { faPlay, faStop } from "@fortawesome/pro-regular-svg-icons";

import { Chip, LoadingOverlay, TableColumn, TableComponent } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Icon } from "@/components/Icon/Icon";
import {
	useDeleteMusicTrackMutation,
	useGetMusicTracksQuery,
	useUpdateMusicTrackMutation,
} from "@/pages/Private/redux/media/media.api";
import { MusicTrack } from "@/pages/Private/redux/media/media.schema";

import { MusicTrackModal } from "./MusicTrackModal";

const formatDuration = (seconds?: number | null) => {
	if (!seconds) {
		return "—";
	}

	return `${Math.floor(seconds / 60)}:${String(Math.round(seconds % 60)).padStart(2, "0")}`;
};

/**
 * The background-music library — global content, and platform-admin territory for exactly
 * the reason global format presets are: every company picks from this one list.
 *
 * A track arrives either as an upload or as a generation through ElevenLabs Music, and the
 * table deliberately does not distinguish them beyond a badge: once a row exists it is a
 * track like any other, with a licence on file either way.
 */
export const MusicLibraryTable: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string, options?: Record<string, unknown>) =>
		t(`mediaPresets.music.${key}`, options ?? {});
	const ta = (key: string) => t(`mediaPresets.${key}`);

	const [page, setPage] = useState(0);
	const [limit, setLimit] = useState(25);
	const [editing, setEditing] = useState<MusicTrack | null>(null);
	const [mode, setMode] = useState<"upload" | "generate" | null>(null);
	const [playingId, setPlayingId] = useState<number | null>(null);
	const [audio, setAudio] = useState<HTMLAudioElement | null>(null);

	// Disabled rows are only visible to a platform admin, which is the only person on this
	// page — the wizard's own picker asks the same endpoint without the flag.
	const { data: tracks, isLoading } = useGetMusicTracksQuery({ includeDisabled: true });
	const [updateTrack] = useUpdateMusicTrackMutation();
	const [deleteTrack, { isLoading: isDeleting }] = useDeleteMusicTrackMutation();

	/** One sample at a time: two tracks over each other tell the listener nothing. */
	const togglePlay = (track: MusicTrack) => {
		audio?.pause();

		if (playingId === track.id || !track.url) {
			setAudio(null);
			setPlayingId(null);

			return;
		}

		const next = new Audio(track.url);

		next.onended = () => setPlayingId(null);
		setAudio(next);
		setPlayingId(track.id);
		void next.play();
	};

	const columns: TableColumn<MusicTrack>[] = useMemo(
		() => [
			{
				label: ts("columns.track"),
				minWidth: 220,
				format: (row) => (
					<Stack direction="column">
						<Typography className="text-ink" variant="subtitle2">
							{row.name}
						</Typography>
						<Typography className="text-subtle" fontWeight="400" variant="body2">
							{row.description || "—"}
						</Typography>
					</Stack>
				),
			},
			{
				label: ts("columns.origin"),
				minWidth: 130,
				format: (row) => (
					<Chip
						color={row.aiGenerated ? "secondary" : "success"}
						label={row.aiGenerated ? ts("origin.generated") : ts("origin.uploaded")}
					/>
				),
			},
			{
				label: ts("columns.duration"),
				minWidth: 100,
				format: (row) => formatDuration(row.durationSeconds),
			},
			{
				label: ts("columns.licence"),
				minWidth: 220,
				format: (row) => (
					<Typography className="text-text" fontWeight="400" variant="body2">
						{row.licence || ts("licenceMissing")}
					</Typography>
				),
			},
			{
				label: ta("columns.status"),
				minWidth: 110,
				format: (row) => (
					<Chip
						color={row.enabled === false ? "secondary" : "success"}
						label={row.enabled === false ? ta("status.disabled") : ta("status.enabled")}
					/>
				),
			},
			{
				label: "",
				minWidth: 260,
				align: "right",
				format: (row) => (
					<Stack direction="row" justifyContent="flex-end" spacing={1}>
						{!!row.url && (
							<Button
								color={ButtonColor.ACTION_SECONDARY}
								image={<Icon icon={playingId === row.id ? faStop : faPlay} />}
								size={ButtonSize.XS}
								title={playingId === row.id ? ts("actions.stop") : ts("actions.play")}
								type="button"
								onClick={() => togglePlay(row)}
							/>
						)}
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							size={ButtonSize.XS}
							title={ta("actions.edit")}
							type="button"
							onClick={() => setEditing(row)}
						/>
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							size={ButtonSize.XS}
							title={row.enabled === false ? ta("actions.enable") : ta("actions.disable")}
							type="button"
							onClick={() => updateTrack({ id: row.id, enabled: row.enabled === false })}
						/>
						<Button
							color={ButtonColor.DANGER}
							size={ButtonSize.XS}
							title={ta("actions.delete")}
							type="button"
							onClick={() => deleteTrack(row.id)}
						/>
					</Stack>
				),
			},
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[t, playingId]
	);

	const withoutLicence = (tracks ?? []).filter((track) => !track.licence?.trim()).length;

	return (
		<section className="rounded-card border border-border-soft bg-white p-5">
			<div className="mb-4 flex flex-wrap items-start justify-between gap-3">
				<div>
					<h3 className="text-ink">{ts("title")}</h3>
					<p className="mt-1 text-sm text-subtle">{ts("description")}</p>
				</div>
				<Stack direction="row" spacing={1}>
					<Button
						className="!w-auto"
						color={ButtonColor.ACTION_SECONDARY}
						size={ButtonSize.S}
						title={ts("actions.generate")}
						type="button"
						onClick={() => setMode("generate")}
					/>
					<Button
						className="!w-auto"
						color={ButtonColor.BRAND}
						size={ButtonSize.S}
						title={ts("actions.upload")}
						type="button"
						onClick={() => setMode("upload")}
					/>
				</Stack>
			</div>

			{/* A track with no licence on file is the exact risk the field exists to close, so
			    the page says so rather than letting it sit unnoticed in a row. */}
			{!!withoutLicence && (
				<div className="mb-4 rounded-card border border-status-warning bg-status-warning-light px-4 py-3">
					<p className="text-sm font-semibold text-warning-ink">
						{ts("licenceWarning", { times: withoutLicence })}
					</p>
				</div>
			)}

			{isLoading || isDeleting ? <LoadingOverlay /> : null}

			<TableComponent<MusicTrack>
				columns={columns}
				data={tracks ?? []}
				handleChangeLimit={(event) => {
					setLimit(+event.target.value);
					setPage(0);
				}}
				handleChangePage={(_, nextPage) => setPage(nextPage)}
				isLoading={isLoading}
				itemCount={tracks?.length ?? 0}
				limit={limit}
				noDataText={ts("empty")}
				page={page}
			/>

			<MusicTrackModal
				handleClose={() => {
					setMode(null);
					setEditing(null);
				}}
				isOpened={Boolean(mode) || Boolean(editing)}
				mode={editing ? "edit" : (mode ?? "upload")}
				track={editing}
			/>
		</section>
	);
};
